import { useEffect, useState } from 'react';
import { apiService } from '../services/api';
import authService from '../services/auth';

const STORAGE_KEY = 'qc_coffee_shops_cache_v1';
const TTL_MS = 15 * 60 * 1000;

let memoryShops = null;
let inflight = null;
const listeners = new Set();

function emit(shops) {
  listeners.forEach((listener) => {
    try {
      listener(shops);
    } catch (error) {
      console.warn('[CoffeeShops] listener failed', error);
    }
  });
}

function persist(shops) {
  memoryShops = shops;
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), shops }),
    );
  } catch (error) {
    console.warn('[CoffeeShops] Не удалось сохранить кэш', error);
  }
  emit(shops);
}

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.shops)) {
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn('[CoffeeShops] Повреждённый кэш, игнорируем', error);
    return null;
  }
}

export function getCachedCoffeeShops() {
  if (memoryShops) {
    return memoryShops;
  }
  const stored = readStorage();
  if (!stored) {
    return null;
  }
  memoryShops = stored.shops;
  return stored.shops;
}

export function isCoffeeShopsCacheFresh() {
  const stored = readStorage();
  if (!stored?.savedAt) {
    return false;
  }
  return Date.now() - stored.savedAt < TTL_MS;
}

export function subscribeCoffeeShops(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Stale-while-revalidate: сразу отдаёт localStorage, параллельно обновляет с API.
 */
export async function prefetchCoffeeShops({ force = false } = {}) {
  const cached = getCachedCoffeeShops();

  if (!force && cached && isCoffeeShopsCacheFresh() && inflight) {
    return cached;
  }

  if (inflight) {
    return cached || inflight;
  }

  if (!authService.checkAuth()) {
    return cached || [];
  }

  inflight = apiService
    .getCoffeeShops()
    .then((shops) => {
      const list = Array.isArray(shops) ? shops : [];
      persist(list);
      return list;
    })
    .finally(() => {
      inflight = null;
    });

  if (!force && cached) {
    return cached;
  }

  return inflight;
}

export function useCoffeeShops() {
  const initial = getCachedCoffeeShops();
  const [shops, setShops] = useState(initial || []);
  const [loading, setLoading] = useState(!initial);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeCoffeeShops((next) => {
      setShops(next);
      setLoading(false);
      setError(null);
    });

    prefetchCoffeeShops()
      .then((list) => {
        setShops(list);
        setError(null);
      })
      .catch((err) => {
        if (!getCachedCoffeeShops()) {
          setError(err.message || 'Не удалось загрузить кофейни');
        }
      })
      .finally(() => {
        setLoading(false);
      });

    return unsubscribe;
  }, []);

  return {
    shops,
    loading,
    error,
    refresh: () => prefetchCoffeeShops({ force: true }),
  };
}
